// ==========================
// Version 35 — components/Navbar.js
// - Updates logged-in "Home" link to "/home" (Landing stays at "/")
// - Adds "Home" link for logged-out users pointing to "/" (optional but helpful)
// - Admin detection supports BOTH shapes: data.isAdmin OR data.roles.admin
// - Keeps Thirdweb wallet controls isolated inside lazy WalletConnect
// - Restyles account dropdown to match BWK navy/gold UI
// - On landing route, hides global navbar at first load and fades it in after scroll
// - Adds mobile hamburger behavior for the authenticated app nav
// - Constrains the account/wallet panel so it cannot overflow small screens
// - Promotes Wallet to a visible nav action and adds outside-click close for hamburger
// - Makes mobile outside-click closing layered: wallet/account panel first, hamburger second
// - Prefers display name over email in visible nav identity text
// - Adds a framed BWK logo treatment to the global navbar
// ==========================
import React from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from '../utils/toast';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import BrandLogo from './BrandLogo';

const WalletConnectLazy = React.lazy(() => import('./WalletConnect.jsx'));

const NAVY = '#0A1A33';
const GOLD = '#C9A44C';
const IVORY = '#FDFCF9';
const WARM_GRAY = '#888888';

function isAnyDialogOpen() {
  try {
    const isVisible = (el) => {
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      const style = window.getComputedStyle(el);
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        rect.width > 0 &&
        rect.height > 0
      );
    };

    if ([...document.querySelectorAll('[role="dialog"], [aria-modal="true"]')].some(isVisible)) {
      return true;
    }
    if ([...document.querySelectorAll('[data-radix-portal]')].some(isVisible)) return true;
    if (
      [
        ...document.querySelectorAll(
          '[data-thirdweb], [data-tw], [class*="thirdweb"], [id*="thirdweb"]'
        ),
      ].some(isVisible)
    ) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

const Navbar = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [loggingOut, setLoggingOut] = React.useState(false);
  const [openPanel, setOpenPanel] = React.useState(null);
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [landingNavVisible, setLandingNavVisible] = React.useState(false);
  const isMobileNav = useMediaQuery('(max-width: 760px)');

  const avatarRef = React.useRef(null);
  const walletButtonRef = React.useRef(null);
  const navRef = React.useRef(null);
  const mobileMenuButtonRef = React.useRef(null);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    if (currentUser?.bwakLocalMock) {
      setIsAdmin(currentUser.bwakLocalMockAdmin === true);
      return;
    }

    if (!currentUser?.uid) {
      setIsAdmin(false);
      return;
    }

    const ref = doc(db, 'users', currentUser.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) return setIsAdmin(false);
        const data = snap.data() || {};
        const admin =
          data?.isAdmin === true || data?.roles?.admin === true;
        setIsAdmin(admin);
      },
      () => setIsAdmin(false)
    );

    return unsub;
  }, [currentUser?.bwakLocalMock, currentUser?.bwakLocalMockAdmin, currentUser?.uid]);

  // Close menu on outside click (BUT NOT if any dialog is open)
  React.useEffect(() => {
    if (!openPanel) return;

    const onMouseDown = (e) => {
      if (isAnyDialogOpen()) return;

      if (avatarRef.current?.contains(e.target)) return;
      if (walletButtonRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;

      setOpenPanel(null);
    };

    const onKey = (e) => {
      if (e.key === 'Escape') setOpenPanel(null);
    };

    document.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [openPanel]);

  React.useEffect(() => {
    if (!mobileNavOpen) return;

    const onPointerDown = (e) => {
      if (mobileMenuButtonRef.current?.contains(e.target)) return;
      if (navRef.current?.contains(e.target)) return;
      if (openPanel) {
        setOpenPanel(null);
        return;
      }
      setMobileNavOpen(false);
    };

    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (openPanel) {
          setOpenPanel(null);
          return;
        }
        setMobileNavOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('touchstart', onPointerDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('touchstart', onPointerDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [mobileNavOpen, openPanel]);

  // If user logs out / auth disappears, close the menu
  React.useEffect(() => {
    if (!currentUser) {
      setOpenPanel(null);
      setMobileNavOpen(false);
    }
  }, [currentUser]);

  const isLandingRoute =
    location.pathname === '/' ||
    location.pathname === '/landingpage-3';

  React.useEffect(() => {
    if (!isLandingRoute) {
      setLandingNavVisible(false);
      return undefined;
    }

    const update = () => setLandingNavVisible(window.scrollY > 72);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, [isLandingRoute]);

  React.useEffect(() => {
    setMobileNavOpen(false);
    setOpenPanel(null);
  }, [location.pathname]);

  React.useEffect(() => {
    if (!isMobileNav) setMobileNavOpen(false);
  }, [isMobileNav]);

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      await logout();
      toast.success('Logged out');
      navigate('/login');
    } catch {
      toast.error('Logout failed');
    } finally {
      setLoggingOut(false);
      setOpenPanel(null);
      setMobileNavOpen(false);
    }
  };

  const onAvatarClick = () => {
    if (isAnyDialogOpen()) return;
    setOpenPanel((value) => (value === 'account' ? null : 'account'));
  };

  const onWalletClick = () => {
    if (isAnyDialogOpen()) return;
    setOpenPanel((value) => (value === 'wallet' ? null : 'wallet'));
  };

  const toggleMobileNav = () => {
    setMobileNavOpen((value) => !value);
    setOpenPanel(null);
  };

  const email = currentUser?.email || '';
  const displayName = currentUser?.displayName?.trim?.() || '';
  const identityLabel = displayName || email;
  const identityTitle = displayName && email ? `${displayName} (${email})` : identityLabel;

  return (
    <header
      style={{
        ...styles.header,
        ...(isLandingRoute ? styles.headerLanding : {}),
        ...(isLandingRoute && !landingNavVisible ? styles.headerLandingHidden : {}),
        ...(isLandingRoute && landingNavVisible ? styles.headerLandingVisible : {}),
      }}
    >
      <div style={styles.inner}>
        {/* Landing is at "/" */}
        <Link to="/" style={styles.logo} aria-label="Home" title="Home">
          <BrandLogo size={44} decorative framed style={styles.logoMark} />
        </Link>

        {isMobileNav ? (
          <button
            ref={mobileMenuButtonRef}
            type="button"
            onClick={toggleMobileNav}
            style={styles.mobileMenuButton}
            aria-label={mobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileNavOpen ? 'true' : 'false'}
            aria-controls="bwak-primary-nav"
          >
            <span style={styles.mobileMenuLine} />
            <span style={styles.mobileMenuLine} />
            <span style={styles.mobileMenuLine} />
          </button>
        ) : null}

        <nav
          ref={navRef}
          id="bwak-primary-nav"
          style={{
            ...styles.nav,
            ...(isMobileNav ? styles.navMobile : {}),
            ...(isMobileNav && mobileNavOpen ? styles.navMobileOpen : {}),
            ...(isMobileNav && !mobileNavOpen ? styles.navMobileClosed : {}),
          }}
          aria-label="Primary"
        >
          {!currentUser ? (
            <>
              <NavItem to="/" label="Home" />
              <NavItem to="/map" label="Global Map" />
              <NavItem to="/login" label="Login" />
              <NavItem to="/register" label="Register" />
            </>
          ) : (
            <>
              <NavItem to="/home" label="Home" />
              <NavItem to="/map" label="Global Map" />
              <NavItem to="/profile" label="Profile" />
              <button
                ref={walletButtonRef}
                type="button"
                onClick={onWalletClick}
                style={{
                  ...styles.navAction,
                  ...(openPanel === 'wallet' ? styles.navActionActive : {}),
                  ...(isMobileNav ? styles.navActionMobile : {}),
                }}
                aria-haspopup="menu"
                aria-expanded={openPanel === 'wallet' ? 'true' : 'false'}
              >
                Wallet
              </button>
              {isAdmin && <NavItem to="/admin" label="Admin" />}

              <span style={styles.identity} title={identityTitle}>
                {identityLabel}
              </span>

              {isMobileNav ? (
                <button
                  type="button"
                  onClick={handleLogout}
                  disabled={loggingOut}
                  style={styles.mobileLogoutBtn}
                >
                  {loggingOut ? 'Logging out...' : 'Logout'}
                </button>
              ) : null}

              <div style={{ position: 'relative' }}>
                <button
                  ref={avatarRef}
                  type="button"
                  onClick={onAvatarClick}
                  style={{
                    ...styles.avatarBtn,
                    ...(isMobileNav ? styles.avatarBtnMobile : {}),
                  }}
                  aria-haspopup="menu"
                  aria-expanded={openPanel === 'account' ? 'true' : 'false'}
                  title="Account"
                >
                  <MiniAvatar
                    photoURL={currentUser.photoURL}
                    displayName={currentUser.displayName}
                    email={email}
                  />
                </button>

                <div
                  ref={menuRef}
                  style={{
                    ...styles.menu,
                    ...(isMobileNav ? styles.menuMobile : {}),
                    ...(openPanel ? styles.menuOpen : styles.menuClosed),
                  }}
                  role="menu"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {openPanel === 'account' ? (
                    <div style={styles.accountPanel}>
                      <div style={styles.accountName} title={identityTitle}>
                        {identityLabel || 'Signed in'}
                      </div>
                      {displayName && email ? (
                        <div style={styles.accountEmail} title={email}>
                          {email}
                        </div>
                      ) : null}
                      <button
                        type="button"
                        onClick={handleLogout}
                        disabled={loggingOut}
                        style={styles.menuBtnDanger}
                        role="menuitem"
                      >
                        {loggingOut ? 'Logging out...' : 'Logout'}
                      </button>
                    </div>
                  ) : null}

                  {openPanel === 'wallet' ? (
                    <div style={{ padding: 6 }}>
                      <React.Suspense
                        fallback={
                          <button style={styles.menuBtnGhost} disabled>
                            Loading wallet...
                          </button>
                        }
                      >
                        <WalletConnectLazy />
                      </React.Suspense>
                    </div>
                  ) : null}
                </div>
              </div>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

function useMediaQuery(query) {
  const [matches, setMatches] = React.useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener?.('change', update);
    media.addListener?.(update);
    return () => {
      media.removeEventListener?.('change', update);
      media.removeListener?.(update);
    };
  }, [query]);

  return matches;
}

function MiniAvatar({ photoURL, displayName, email }) {
  const fallback = (
    displayName?.trim()?.[0] || email?.trim()?.[0] || '🙂'
  ).toUpperCase();

  return photoURL ? (
    <img
      src={photoURL}
      alt="Profile"
      width={30}
      height={30}
      style={styles.avatarImg}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
    />
  ) : (
    <div style={styles.avatarFallback} aria-hidden="true">
      {fallback}
    </div>
  );
}

const NavItem = ({ to, label }) => (
  <NavLink
    to={to}
    style={({ isActive }) => ({
      ...styles.link,
      ...(isActive ? styles.linkActive : {}),
    })}
  >
    {label}
  </NavLink>
);

const styles = {
  header: {
    position: 'sticky',
    top: 0,
    background: NAVY,
    borderBottom: '1px solid rgba(201,164,76,0.35)',
    boxShadow: 'none',
    zIndex: 100,
  },
  headerLanding: {
    position: 'fixed',
    left: 0,
    right: 0,
    background: 'rgba(10,26,51,0.88)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    transition: 'opacity 260ms ease, transform 260ms ease',
  },
  headerLandingHidden: {
    opacity: 0,
    pointerEvents: 'none',
    transform: 'translateY(-100%)',
  },
  headerLandingVisible: {
    opacity: 1,
    pointerEvents: 'auto',
    transform: 'translateY(0)',
  },
  inner: {
    maxWidth: 1120,
    margin: '0 auto',
    padding: '10px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    position: 'relative',
  },
  logo: {
    color: IVORY,
    textDecoration: 'none',
    lineHeight: 1,
    display: 'inline-flex',
    alignItems: 'center',
  },
  logoMark: {
    background: 'linear-gradient(180deg, rgba(10,26,51,0.90), rgba(6,14,28,0.96))',
    border: '1px solid rgba(201,164,76,0.58)',
    boxShadow:
      '0 10px 26px rgba(0,0,0,0.26), 0 0 0 1px rgba(253,252,249,0.08), inset 0 1px 0 rgba(253,252,249,0.08)',
  },
  nav: { display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' },
  navMobile: {
    position: 'absolute',
    top: 'calc(100% + 10px)',
    left: 12,
    right: 12,
    display: 'grid',
    gap: 10,
    alignItems: 'stretch',
    background: 'rgba(10,26,51,0.98)',
    border: '1px solid rgba(201,164,76,0.42)',
    borderRadius: 14,
    padding: 12,
    boxShadow: '0 18px 46px rgba(0,0,0,0.42)',
    zIndex: 999,
  },
  navMobileOpen: {
    visibility: 'visible',
    opacity: 1,
    transform: 'translateY(0)',
    pointerEvents: 'auto',
  },
  navMobileClosed: {
    visibility: 'hidden',
    opacity: 0,
    transform: 'translateY(-6px)',
    pointerEvents: 'none',
  },
  link: { color: IVORY, textDecoration: 'none', fontWeight: 600 },
  linkActive: {
    color: NAVY,
    background: GOLD,
    padding: '4px 8px',
    borderRadius: 8,
  },
  navAction: {
    border: '1px solid rgba(201,164,76,0.46)',
    background: 'rgba(201,164,76,0.10)',
    color: IVORY,
    borderRadius: 8,
    padding: '5px 10px',
    font: 'inherit',
    fontWeight: 700,
    lineHeight: 1.2,
    cursor: 'pointer',
  },
  navActionActive: {
    color: NAVY,
    background: GOLD,
    borderColor: GOLD,
  },
  navActionMobile: {
    width: '100%',
    textAlign: 'left',
    padding: '10px 12px',
  },
  identity: {
    color: WARM_GRAY,
    fontSize: 13,
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  avatarBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  avatarBtnMobile: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: 4,
  },
  mobileMenuButton: {
    width: 42,
    height: 42,
    borderRadius: 10,
    border: '1px solid rgba(201,164,76,0.48)',
    background: 'rgba(10,26,51,0.72)',
    color: IVORY,
    display: 'grid',
    placeItems: 'center',
    gap: 0,
    padding: 9,
    cursor: 'pointer',
  },
  mobileMenuLine: {
    width: 20,
    height: 2,
    borderRadius: 2,
    background: GOLD,
    display: 'block',
  },
  mobileLogoutBtn: {
    width: '100%',
    border: '1px solid rgba(183,110,121,0.58)',
    color: '#F6D8DD',
    padding: '10px 12px',
    borderRadius: 10,
    background: 'rgba(92,26,43,0.20)',
    fontWeight: 800,
    textAlign: 'left',
    cursor: 'pointer',
  },

  avatarImg: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    objectFit: 'cover',
    border: `1px solid ${GOLD}`,
    boxShadow: '0 2px 10px rgba(10,26,51,0.25)',
    background: '#fff',
    display: 'block',
  },

  avatarFallback: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: IVORY,
    color: NAVY,
    display: 'grid',
    placeItems: 'center',
    fontWeight: 800,
    border: `1px solid ${GOLD}`,
    boxShadow: '0 2px 10px rgba(10,26,51,0.25)',
  },

  menu: {
    position: 'absolute',
    right: 0,
    top: '110%',
    background: NAVY,
    color: IVORY,
    borderRadius: 12,
    padding: 10,
    width: 'min(360px, calc(100vw - 24px))',
    maxWidth: 'calc(100vw - 24px)',
    border: '1px solid rgba(201,164,76,0.55)',
    boxShadow: '0 18px 46px rgba(0,0,0,0.45)',
    zIndex: 1000,
  },
  menuMobile: {
    position: 'fixed',
    left: 12,
    right: 12,
    top: 68,
    width: 'auto',
    maxWidth: 'none',
    maxHeight: 'calc(100dvh - 84px)',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
  },

  menuOpen: { display: 'block' },
  menuClosed: { display: 'none' },
  accountPanel: {
    display: 'grid',
    gap: 10,
  },
  accountName: {
    color: IVORY,
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1.35,
    overflowWrap: 'anywhere',
    padding: '2px 2px 0',
  },
  accountEmail: {
    color: WARM_GRAY,
    fontSize: 12,
    lineHeight: 1.35,
    overflowWrap: 'anywhere',
    padding: '2px 2px 0',
  },

  menuBtnDanger: {
    width: '100%',
    border: '1px solid rgba(229,77,46,0.85)',
    color: '#FFD2C8',
    padding: 10,
    borderRadius: 10,
    background: 'rgba(229,77,46,0.12)',
    fontWeight: 800,
    cursor: 'pointer',
  },
  menuBtnGhost: {
    width: '100%',
    padding: 10,
    borderRadius: 10,
    border: '1px dashed rgba(201,164,76,0.45)',
    background: 'rgba(255,255,255,0.06)',
    color: IVORY,
  },
};

export default Navbar;
// ==========================
// End Version 35 — Navbar.js
// ==========================
